<template>
  <div class="main-page-container">
    <AlertFoundActiveSession />
    <b-modal
      id="exportingProgress"
      hide-footer
      hide-header
      size="lg"
      :scrollable="false"
    >
      <ExportProgress />
    </b-modal>

    <PageHeader
      :title="title"
      :hasHeaderButton="true"
      class="page-top-header d-flex align-items-center vp-width-270"
    >
      <template #headerButton>
        <button class="cursor-pointer" @click="redirectTo('attendance-mode')">
          {{ $t("common.add") }}
        </button>
      </template>
    </PageHeader>
    <!-- grid -->
    <section
      id="reasons-mode"
      class="attendance-mode-list-white-bg-sec"
      style="background-color: white"
    >
      <DxTreeList
        id="allModesGrid"
        :ref="dataGridRefName"
        :data-source="allAttendanceModes"
        :no-data-text="$t('common.grid.noRecordsYet')"
        :show-borders="false"
        :show-column-lines="true"
        :show-row-lines="true"
        :column-auto-width="true"
        :word-wrap-enabled="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :repaint-changes-only="false"
        :two-way-binding-enabled="true"
        :expanded-row-keys="expandedRowKeys"
        :selected-row-keys="selectedRowKeys"
        :allow-column-reordering="true"
        :class="{
          attendanceGridHeightWithAlert: getActiveSessionsCount,
          attendanceGridHeight: !getActiveSessionsCount,
        }"
        key-expr="id"
        parent-id-expr="mod_Parent_ID"
        @content-ready="contentReady($event)"
        @editor-preparing="onEditorPreparing($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
      >
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxHeaderFilter :visible="true" />
        <!-- <DxSelection mode="multiple" /> -->
        <DxColumnChooser
          :enabled="true"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />
        <DxFilterRow :visible="true" />
        <!-- <DxLoadPanel :enabled="true"> </DxLoadPanel> -->

        <!-- start::Toolbar -->
        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem template="exportButtonTemplate" location="before" />
          <DxItem template="clearAllTemplate" location="before" />
        </DxToolbar>
        <!-- end::Toolbar -->

        <!-- start::Pager -->
        <!-- <DxPaging :enabled="true" :page-size="10" /> -->
        <DxPager
          :visible="false"
          displayMode="full"
          :show-page-size-selector="false"
          :allowed-page-sizes="['all']"
          :show-info="true"
          :show-navigation-buttons="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />
        <!-- end::Pager -->

        <!-- start::scrolling -->
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />
        <!-- end::scrolling -->

        <!-- start::Column Button Template -->
        <template #columnButtonTemplate>
          <button class="custom-columns" @click="dataGrid.showColumnChooser()">
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>

            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>
        <!-- end::Column Button Template -->

        <!-- start::Export Button -->
        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            @item-click="serverSideExport"
            :text="$t('common.grid.export')"
          />
        </template>
        <!-- end::Export Button -->

        <!-- start::Clear All Button Template -->
        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="clearFilter"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>
        <!-- end::Clear All Button Template -->

        <DxColumn
          data-field="name"
          class="first-column"
          :caption="$t('common.name')"
          :width="358"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          data-type="string"
          :allow-reordering="false"
        />

        <DxColumn
          data-field="type"
          :caption="$t('common.type')"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          :visible="true"
          :width="118"
          data-type="string"
        />

        <DxColumn
          data-field="integration_name"
          :caption="$t('attendanceModes.grid.integrationName')"
          :allow-editing="false"
          :visible="true"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          :width="181"
          data-type="string"
        />

        <DxColumn
          data-field="working_days"
          :caption="$t('attendanceModes.grid.workingDays')"
          :visible="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="175"
          data-type="string"
        />

        <DxColumn
          data-field="from_time"
          :caption="$t('attendanceModes.from')"
          :allow-editing="false"
          :visible="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="118"
          data-type="string"
        />

        <DxColumn
          data-field="to_time"
          :caption="$t('attendanceModes.to')"
          :allow-editing="false"
          :visible="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="118"
          data-type="string"
        />

        <DxColumn
          data-field="late_sign_in"
          :caption="$t('attendanceModes.grid.lateSignIn')"
          :visible="true"
          :width="128"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="early_sign_out"
          :caption="$t('attendanceModes.grid.earlySignOut')"
          :visible="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="132"
          data-type="string"
        />

        <DxColumn
          data-field="duration"
          :caption="$t('attendanceModes.grid.duration')"
          :visible="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :width="118"
          data-type="string"
        />

        <DxColumn
          data-field="Actions"
          :caption="$t('common.grid.actions')"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          :visible="true"
          css-class="select-actions select-actionsDAsh"
          cell-template="action-cell"
          :width="190"
          data-type="object"
          :allow-reordering="false"
        />

        <template #action-cell="{ data }">
          <div
            class="d-flex align-items-center"
            v-if="data.data.row == 'mode' || data.data.row == 'reason'"
          >
            <Actions
              :row="data.data.row"
              :rowData="data.data"
              @refreshAfterDelete="refreshModesGrid"
            />
          </div>
        </template>
      </DxTreeList>
    </section>
  </div>
</template>

<script>
const dataGridRefName = "dataGridAttendanceModes";
const subGridRef = "subGridAttendanceModes";
import { mapActions, mapGetters } from "vuex";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import Actions from "@/components/attendanceMode/actionsForAttendanceModes";
import moment from "moment";
import "jspdf-autotable";
import { default as Button } from "devextreme-vue/button";
import ExportProgress from "@/components/grid/ExportProgress";
import { attendanceModesColumns } from "@/utils/dataSource";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";
import PageHeader from "@/components/page-header";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

import {
  DxTreeList,
  DxColumn,
  DxColumnChooser,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxFilterRow,
  // DxPaging,
  DxPager,
  DxScrolling,
  //   DxLoadPanel,
  //   DxSelection,
  //   DxLookup,
  DxStateStoring,
} from "devextreme-vue/tree-list";

export default {
  name: "attendanceModes",
  title: "Attendance Modes",
  components: {
    DxTreeList,
    PageHeader,
    // DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    // DxExport,
    DxStateStoring,
    DxToolbar,
    DxItem,
    DxDropDownButton,
    // DxPaging,
    DxPager,
    // DxSorting,
    Button,
    DxScrolling,
    // DxMasterDetail,
    Actions,
    ExportProgress,
    AlertFoundActiveSession,
  },
  data() {
    return {
      rtlEnabled: false,
      // title: "Attendance Modes",
      allAttendanceModes: [],
      items: [
        {
          text: "User Settings",
        },
        {
          text: "Attendance Modes",
          active: true,
        },
      ],
      dataGridRefName,
      subGridRef,
      allowedOperations: ["contains"],
      selectedOperation: "contains", // default icon
      isServerSideExport: false,
      buttonData: ["Excel", "CSV", "PDF"],
      expandedRowKeys: [1, 2],
      selectedRowKeys: [1, 29, 42],
      loggedUser: "",
      showClearAll: false,
      columnsFilters: [],
      tz: moment.tz.guess(),
      integration_ids: [],
    };
  },
  methods: {
    ...mapActions("grid", [
      "createTableHeaders",
      "onTableHeader",
      "updateTableHeaders",
      "exportJobBE",
    ]),
    ...mapActions("attendanceMode", ["fetchAttendanceModes"]),
    ...mapActions("integration", ["fetchWondeIntegrations"]),
    onRowPrepared(e) {
      if (e.rowType == "data" && e.data.reasons.length == 0) {
        e.rowElement
          .querySelector(".dx-command-expand")
          .firstChild.classList.remove("dx-datagrid-group-closed");
        e.rowElement
          .querySelector(".dx-command-expand")
          .classList.remove("dx-datagrid-expand");
      }
    },

    onRowPreparedSub(e) {
      if (e.rowType == "data" && e.data.attendance_codes.length == 0) {
        e.rowElement
          .querySelector(".dx-command-expand")
          .firstChild.classList.remove("dx-datagrid-group-closed");
        e.rowElement
          .querySelector(".dx-command-expand")
          .classList.remove("dx-datagrid-expand");
      }
    },

    clearFilter() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
    },

    serverSideExport(e) {
      this.exportJobBE({
        filterBy: this.columnsFilters,
        orderBy: "",
        type: e.itemData.toLowerCase(),
        gridName: "attendance_mode",
      });
    },

    getIntgName(rowsData) {
      if (!rowsData.integration_name) return "-";
      return rowsData.integration_name;
    },

    getWorkingDays(rowsData) {
      return rowsData.working_days.join(" - ");
    },

    // getWorkingDaysForReason(rowsData) {
    //   let modeIndex;
    //   this.getAttendanceModes.map((mode, i) => {
    //     const reason = mode.reasons.find((reason) => reason.id == rowsData.id);
    //     if (reason) modeIndex = i;
    //   });
    //   if (modeIndex)
    //     return this.getAttendanceModes[modeIndex].working_days.join(" - ");
    // },

    // getWorkingDaysForCode(rowsData) {
    //   let modeIndex;
    //   this.getAttendanceModes.map((mode, i) => {
    //     mode.reasons.map((reason) => {
    //       const code = reason.attendance_codes.find(
    //         (code) => code.id == rowsData.id
    //       );
    //       if (code) {
    //         modeIndex = i;
    //       }
    //     });
    //   });
    //   if (modeIndex)
    //     return this.getAttendanceModes[modeIndex].working_days.join(" - ");
    // },

    getType(rowsData) {
      if (!rowsData.type) return "-";
      const type = rowsData.type.split("_").join(" ");
      return type.charAt(0).toUpperCase() + type.slice(1);
    },

    getCodeType(rowsData) {
      if (!rowsData.type) return "-";
      const type = rowsData.type.toLowerCase();
      return type.charAt(0).toUpperCase() + type.slice(1);
    },

    getSignIn(rowsData) {
      // if (rowsData.settings && +rowsData.settings[3].pivot.value)
      //   return rowsData.settings[3].pivot.value + "m";
      // return "-";
      let signInTimeString = "-";
      if (rowsData.settings && rowsData.settings.length) {
        rowsData.settings.map((setting) => {
          if (setting.name == "allow_late_sign_in_time" && setting.pivot.value)
            signInTimeString = setting.pivot.value + "m";
        });
      }
      return signInTimeString;
    },

    getSignOut(rowsData) {
      let signOutTimeString = "-";
      if (rowsData.settings && rowsData.settings.length) {
        rowsData.settings.map((setting) => {
          if (
            setting.name == "allow_early_sign_out_time" &&
            setting.pivot.value
          )
            signOutTimeString = setting.pivot.value + "m";
        });
      }
      return signOutTimeString;
    },

    getFromTime(rowsData) {
      if (!rowsData.from_time) return "-";
      const todayDate = moment(new Date()).format("YYYY-MM-DD");
      return moment(todayDate + " " + rowsData.from_time).format("hh:mm A");
    },

    getToTime(rowsData) {
      if (!rowsData.to_time) return "-";
      const todayDate = moment(new Date()).format("YYYY-MM-DD");
      return moment(todayDate + " " + rowsData.to_time).format("hh:mm A");
    },

    getDuration(rowsData) {
      if (!rowsData.duration) return "-";
      return rowsData.duration;
    },

    redirectTo(pageName) {
      this.$router.push({ path: `/add-${pageName}` });
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/attendance-modes",
        headers: attendanceModesColumns,
      });
    },
    async customLoad() {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/attendance-modes",
        });
        let value = this.getTableHeaders;
        console.log("customLoad");
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 50;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");
        this.formatingFilterArr(value);
        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/attendance-modes",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/attendance-modes",
            headers: value,
            filters: "[]",
          });
        }
        console.log(
          "customSave value",
          value != JSON.stringify(this.getTableHeaders)
        );
      }
    },

    async onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");
        await exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function (options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.xlsx.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "attendance-modes.xlsx"
            );
          });
        });
        // e.cancel = true;
      } else if (e.itemData == "CSV") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        await exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function (options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.csv.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "attendance-modes.csv"
            );
          });
        });
      } else if (e.itemData == "PDF") {
        const doc = new jsPDF();

        // Add custom font
        doc.addFont(amiriFont, "Amiri", "normal");
        doc.setFont("Amiri");

        doc.autoTable({ direction: "rtl" });

        await exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.dataGrid,
          // showHead: "everyPage",
          customizeCell: function (options) {
            const { gridCell, pdfCell } = options;

            if (gridCell.rowType === "data") {
              // data columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                cellWidth: "auto",
                halign: "center",
              };
            } else {
              // header columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                fillColor: "#2680EB",
                textColor: "#FFFFFF",
                cellWidth: "auto",
                halign: "center", 
              };
            }
          },
          customizeTable: (doc) => {
            doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
          },
        }).then(() => {
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageSize = doc.internal.pageSize;
            const pageWidth = pageSize.width
              ? pageSize.width
              : pageSize.getWidth();
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            const header = this.$t("attendanceModes.title");
            const headerWidth = doc.getTextDimensions(header).w;
            const footer = `${this.$t("common.page")} ${i} ${this.$t("common.of")} ${pageCount}`;

            // Header
            doc.text(header, (pageWidth - headerWidth) / 2, 10);

            // Footer
            doc.text(
              footer,
              pageWidth / 2 - doc.getTextWidth(footer) / 2,
              pageHeight - 5,
              { baseline: "bottom" }
            );
          }
          doc.save("attendance-modes.pdf");
        });
      }
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
    },
    formatingFilterArr(arr) {
      let columns = [];
      arr = JSON.parse(arr);
      arr.columns.map((obj) => {
        if (obj.name != "Actions") {
          delete obj.width;
          delete obj.dataField;
          delete obj.dataType;
          delete obj.filterType;
          columns.push(obj);
        }
      });
      columns.sort((a, b) => {
        return a.visibleIndex - b.visibleIndex;
      });
      console.log("columns", columns);
      this.columnsFilters = columns;
    },
    refreshModesGrid() {
      setTimeout(() => {
        this.fetchAllAttendanceModes({ integration_ids: this.integration_ids });
      }, 500);
    },
    fetchAllAttendanceModes() {
      this.fetchAttendanceModes({ integration_ids: this.integration_ids }).then(
        (data) => {
          // console.log('fetchAttendanceModes', data);
          this.allAttendanceModes = data.data.map((item) => {
            return {
              id: item.id,
              mod_Parent_ID: 0,
              row: "mode",
              name: item.name,
              type: this.getType(item),
              integration_id: item.integration_id,
              integration_name: this.getIntgName(item),
              working_days: this.getWorkingDays(item),
              from_time: this.getFromTime(item),
              to_time: this.getToTime(item),
              settings: item.settings,
              attendance_codes: item.attendance_codes,
              late_sign_in: this.getSignIn(item),
              early_sign_out: this.getSignOut(item),
              duration: this.getDuration(item),
              are_attendance_codes_used: item.are_attendance_codes_used,
              has_flow: item.has_flow,
              has_reasons: item.reasons && item.reasons.length ? true : false,
            };
          });
          data.data.map((item) => {
            if (item.reasons && item.reasons.length > 0) {
              item.reasons.map((reason) => {
                this.allAttendanceModes.push({
                  id: reason.id,
                  mod_Parent_ID: item.id,
                  row: "reason",
                  name: reason.name,
                  type: this.getType(reason),
                  integration_name: this.getIntgName(item),
                  working_days: this.getWorkingDays(item),
                  from_time: this.getFromTime(reason),
                  to_time: this.getToTime(reason),
                  late_sign_in: "-",
                  early_sign_out: "-",
                  duration: this.getDuration(reason),
                  attendance_codes: reason.attendance_codes,
                  presented: reason.presented,
                });

                if (
                  reason.attendance_codes &&
                  reason.attendance_codes.length > 0
                ) {
                  reason.attendance_codes.map((code) => {
                    this.allAttendanceModes.push({
                      id: code.id,
                      mod_Parent_ID: reason.id,
                      row: "code",
                      name: code.label,
                      type: this.getCodeType(code),
                      integration_name: this.getIntgName(item),
                      working_days: this.getWorkingDays(item),
                      from_time: this.getFromTime(code),
                      to_time: this.getToTime(code),
                      late_sign_in: "-",
                      early_sign_out: "-",
                      duration: this.getDuration(code),
                    });
                  });
                }
              });
            }
          });
          this.dataGrid.endCustomLoading();
        }
      );
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-treelist-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
  watch: {
    getExportProgessAppearance(value) {
      value && this.$bvModal.show("exportingProgress");
    },
    // getAttendanceModes(items) {
    //   console.log("items", items);
    //   this.allAttendanceModes = items.map((item) => {
    //     return {
    //       id: item.id,
    //       mod_Parent_ID: 0,
    //       row: "mode",
    //       name: item.name,
    //       type: this.getType(item),
    //       integration_id: item.integration_id,
    //       integration_name: this.getIntgName(item),
    //       working_days: this.getWorkingDays(item),
    //       from_time: this.getFromTime(item),
    //       to_time: this.getToTime(item),
    //       settings: item.settings,
    //       attendance_codes: item.attendance_codes,
    //       late_sign_in: this.getSignIn(item),
    //       early_sign_out: this.getSignOut(item),
    //       duration: this.getDuration(item),
    //       are_attendance_codes_used: item.are_attendance_codes_used,
    //       has_reasons: item.reasons && item.reasons.length ? true : false,
    //     };
    //   });
    //   items.map((item) => {
    //     if (item.reasons && item.reasons.length > 0) {
    //       item.reasons.map((reason) => {
    //         this.allAttendanceModes.push({
    //           id: reason.id,
    //           mod_Parent_ID: item.id,
    //           row: "reason",
    //           name: reason.name,
    //           type: this.getType(reason),
    //           integration_name: this.getIntgName(item),
    //           working_days: this.getWorkingDays(item),
    //           from_time: this.getFromTime(reason),
    //           to_time: this.getToTime(reason),
    //           late_sign_in: "-",
    //           early_sign_out: "-",
    //           duration: this.getDuration(reason),
    //           attendance_codes: reason.attendance_codes,
    //           presented: reason.presented,
    //         });

    //         if (reason.attendance_codes && reason.attendance_codes.length > 0) {
    //           reason.attendance_codes.map((code) => {
    //             this.allAttendanceModes.push({
    //               id: code.id,
    //               mod_Parent_ID: reason.id,
    //               row: "code",
    //               name: code.label,
    //               type: this.getCodeType(code),
    //               integration_name: this.getIntgName(item),
    //               working_days: this.getWorkingDays(item),
    //               from_time: this.getFromTime(code),
    //               to_time: this.getToTime(code),
    //               late_sign_in: "-",
    //               early_sign_out: "-",
    //               duration: this.getDuration(code),
    //             });
    //           });
    //         }
    //       });
    //     }
    //   });
    // },
  },
  computed: {
    ...mapGetters("i18N", ["getLanguage"]),
    // ...mapGetters("attendanceMode", ["getAttendanceModes"]),
    ...mapGetters("authfack", ["userDetails", "currentEntity"]),
    ...mapGetters("grid", ["getTableHeaders", "getExportProgessAppearance"]),
    ...mapGetters("integration", ["getWondeIntegrations"]),
    ...mapGetters("emergency", ["getActiveSessionsCount"]),

    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
    subGrid() {
      return this.$refs[this.subGridRef].instance;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    title() {
      return this.$t("attendanceModes.title");
    },
  },
  async beforeMount() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    await this.fetchWondeIntegrations().then(async (data) => {
      this.dataGrid.beginCustomLoading();
      await data.map(async (integration) => {
        this.integration_ids.push(integration.id);
      });
      await this.fetchAllAttendanceModes({
        integration_ids: this.integration_ids,
      });
    });
  },
  created() {
    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });

    this.loggedUser = this.userDetails.email;
  },
};
</script>
<style lang="scss">
.attendanceGridHeight .dx-treelist-container {
  height: calc(100vh - 197px);
}
.attendanceGridHeightWithAlert .dx-treelist-container {
  height: calc(100vh - 243px);
}
div#allModesGrid {
  min-height: 450px;
}
section#reasons-mode {
  .dx-treelist-header-panel {
    padding: 0px !important;
  }
  .dx-treelist-nodata {
    font-size: 16px !important;
  }

  .dx-treelist-text-content.dx-text-content-alignment-left {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #787878;
  }
  tr.dx-row.dx-column-lines.dx-treelist-filter-row {
    background: #ffffff !important;
  }
  .dx-treelist-text-content {
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;

    color: #495057;
  }
}
</style>
<style lang="scss" scoped>
::v-deep {
  .main-grid-con {
    .gid-view-con {
      width: auto;
      height: 76px;
      background: #ffffff;
      padding-bottom: 15px;
      border-radius: 8px 8px 0px 0px;
      display: flex;
      align-items: end;
      .grid-button-con {
        padding-inline-start: 38px;
        button.gid-view,
        button.calendar-view {
          width: 125px;
          height: 27px;
          background: #103f5e;
          border-radius: 13.5px;
          border: none;

          font-weight: 500;
          font-size: 16px;
          line-height: 19px;
          text-align: center;

          color: #ffffff;
        }
        button.gid-view {
          margin-inline-end: 27px;
        }
        button.calendar-view {
          &:disabled {
            background: #c7c7c7;
          }
        }
      }
    }
    .emptyspace-ubder-btn {
      height: 23px;
      background: #ffffff;
      display: flex;
      align-items: end;
      justify-content: center;

      .sep-btn-line {
        width: 92%;
        height: 1px;
        background: #000000;
        opacity: 0.08;
      }
    }
  }

  // ol.breadcrumb {
  //   margin-bottom: 28px !important;
  // }
  .add-mode {
    .btn-secondary {
      background: #103f5e !important;
      border-radius: 13.5px !important;
      border: none;

      width: 127px !important;
      height: 27px !important;
      font-size: 16px !important;
      line-height: 19px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff !important;
    }
    ul.dropdown-menu.show {
      box-shadow: 0px 4px 4px rgb(0 0 0 / 25%) !important;
      border-radius: 6px !important;
      text-align: center !important;
      min-width: 127px !important;
      margin-top: 6px !important;
      li.unique-mode,
      li.unique-reason {
        a.dropdown-item {
          font-weight: 400;
          font-size: 16px;
          line-height: 19px;
          text-align: center;
          padding-inline-end: 24px;
          color: #787878;
        }
      }
    }
  }
}

button.btn.d-flex.align-items-center.justify-content-center.text-center.add-mode-btn.btn-primary.btn-sm.rounded-pill {
  margin-inline-start: 40px;
  height: 27px;
  width: 127px;

  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #ffffff;
}

.attendance-mode-list-white-bg-sec {
  background-color: white;
  padding-inline-start: 0px;
  // padding-top: 25px;
  border-radius: 8px !important;
}
</style>
